import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import type { Category, Currency, LedgerAccount, WorkspaceIDs } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { localDateTimeValue } from "../../shared/lib/format";
import { DateTimeField } from "../../shared/ui/DateTimeField";
import { Select } from "../../shared/ui/Select";
import { createCategory } from "../categories/api";
import { recordMovement, type MovementKind } from "./api";
import { movementSchema, type MovementForm } from "./model";

interface MovementComposerProps {
  ids: WorkspaceIDs;
  currency: Currency;
  accounts: LedgerAccount[];
  onNotify: (message: string) => void;
  // Account page: every operation starts from this account, no picker shown.
  fixedAccountID?: string;
  categories?: Category[];
  // Inside a disclosure the surrounding panel and heading are already there.
  bare?: boolean;
}

const NEW_CATEGORY = "__new__";

export function MovementComposer({ ids, currency, accounts, onNotify, fixedAccountID, categories = [], bare = false }: MovementComposerProps) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<MovementKind>("expense");
  const [newCategory, setNewCategory] = useState("");
  const assets = accounts;
  // Fall back to the book's first asset account when the workspace names none.
  const initialAccount = fixedAccountID ?? (assets.some((account) => account.id === ids.cash) ? ids.cash : (assets[0]?.id ?? ""));
  const form = useForm<MovementForm>({
    resolver: zodResolver(movementSchema),
    defaultValues: {
      amount: "", fee: "", description: "", effectiveAt: localDateTimeValue(),
      accountId: initialAccount, destinationAccountId: "", categoryId: "",
    },
  });
  const accountID = useWatch({ control: form.control, name: "accountId" });
  const categoryChoice = useWatch({ control: form.control, name: "categoryId" });
  const effectiveAt = useWatch({ control: form.control, name: "effectiveAt" });
  const destinationID = useWatch({ control: form.control, name: "destinationAccountId" });
  const selected = assets.find((account) => account.id === accountID) ?? assets[0];
  const destinations = assets.filter((account) => account.id !== selected?.id && account.currency === selected?.currency);
  useEffect(() => {
    if (kind === "transfer" && !destinations.some((account) => account.id === form.getValues("destinationAccountId"))) {
      form.setValue("destinationAccountId", destinations[0]?.id ?? "");
    }
  }, [destinations, form, kind]);
  const mutation = useMutation({
    mutationFn: async (values: MovementForm) => {
      if (!selected) throw new Error("Open an asset account first.");
      if (kind !== "transfer" && selected.currency !== currency) {
        throw new Error(`Expense and income entry for ${selected.currency} needs an explicit FX amount. Use a ${currency} account for now.`);
      }
      const date = new Date(values.effectiveAt);
      if (Number.isNaN(date.getTime())) throw new Error("Choose a valid transaction date.");
      let categoryId = kind === "transfer" ? "" : values.categoryId;
      if (categoryId === NEW_CATEGORY) {
        const name = newCategory.trim();
        if (!name) throw new Error("Name the new category or pick an existing one.");
        categoryId = (await createCategory(ids.book, name)).category.id;
      }
      return recordMovement(kind, { ...values, categoryId, effectiveAt: date.toISOString() }, ids, selected.currency);
    },
    onSuccess: async () => {
      setNewCategory("");
      form.reset({
        amount: "", fee: "", description: "", effectiveAt: localDateTimeValue(),
        accountId: selected?.id ?? initialAccount, destinationAccountId: "", categoryId: "",
      });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onNotify(kind === "transfer" ? "Transfer recorded." : `${kind === "income" ? "Income" : "Expense"} recorded.`);
    },
  });

  return (
    <section className={bare ? "bare-section" : "panel movement-panel"} aria-labelledby="movement-title">
      <div className="panel-heading">
        {bare ? <span /> : <div><p className="eyebrow">New transaction</p><h2 id="movement-title">Record money</h2></div>}
        <div className="segmented" role="group" aria-label="Transaction type">
          {(["expense", "income", "transfer"] as MovementKind[]).map((value) => (
            <button className={kind === value ? "active" : ""} type="button" onClick={() => setKind(value)} key={value}>{value}</button>
          ))}
        </div>
      </div>
      <form onSubmit={form.handleSubmit((values) => mutation.mutate(values))}>
        {fixedAccountID ? (
          <input type="hidden" {...form.register("accountId")} />
        ) : (
          <div className="field">
            <span className="field-label" id="composer-account">{kind === "income" ? "To account" : "From account"}</span>
            <Select
              labelledBy="composer-account" value={accountID ?? ""} required
              onChange={(id) => form.setValue("accountId", id, { shouldValidate: true })}
              options={assets.map((account) => ({ value: account.id, label: account.name, hint: account.currency }))}
            />
          </div>
        )}
        {kind === "transfer" ? (
          <div className="field">
            <span className="field-label" id="composer-destination">To account</span>
            <Select
              labelledBy="composer-destination" value={destinationID ?? ""} placeholder="Choose destination" required
              onChange={(id) => form.setValue("destinationAccountId", id, { shouldValidate: true })}
              options={destinations.map((account) => ({ value: account.id, label: account.name, hint: account.currency }))}
            />
          </div>
        ) : null}
        <label>{kind === "income" ? "Gross amount" : "Amount"}<span className="amount-input amount-input-large"><input {...form.register("amount")} inputMode="decimal" placeholder="0.00" required /><span>{selected?.currency ?? currency}</span></span></label>
        <label>{kind === "income" ? "Fee withheld" : "Additional fee"}<span className="amount-input"><input {...form.register("fee")} inputMode="decimal" placeholder="0.00" /><span>{selected?.currency ?? currency}</span></span></label>
        <label>Description<input {...form.register("description")} maxLength={200} placeholder={kind === "transfer" ? "Cash deposit" : "Groceries, salary, coffee…"} required /></label>
        {kind !== "transfer" ? (
          <div className="field">
            <span className="field-label" id="composer-category">Category</span>
            <Select
              labelledBy="composer-category" value={categoryChoice ?? ""}
              onChange={(id) => form.setValue("categoryId", id, { shouldValidate: true })}
              options={[
                { value: "", label: "Uncategorized" },
                ...categories.map((category) => ({ value: category.id, label: category.name })),
                { value: NEW_CATEGORY, label: "＋ New category…" },
              ]}
            />
          </div>
        ) : null}
        {kind !== "transfer" && categoryChoice === NEW_CATEGORY ? (
          <label>New category name<input value={newCategory} onChange={(event) => setNewCategory(event.target.value)} maxLength={120} placeholder="Groceries" autoFocus /></label>
        ) : null}
        <DateTimeField label="When" required value={effectiveAt ?? ""}
          onChange={(value) => form.setValue("effectiveAt", value, { shouldValidate: true })} />
        <p className="form-error" role="alert">{Object.values(form.formState.errors)[0]?.message ?? (mutation.error ? humanError(mutation.error) : "")}</p>
        <button className="primary-button" type="submit" disabled={mutation.isPending || assets.length === 0}>
          <span>{mutation.isPending ? "Recording…" : `Record ${kind}`}</span><span aria-hidden="true">→</span>
        </button>
      </form>
    </section>
  );
}
